/**
 * Lead Status Rules
 * Defines required and optional fields for each lead status
 */

/**
 * Fields required for each status
 * These rules govern both status transitions and initial lead creation
 */
export const requiredFieldsByStatus: Record<string, string[]> = {
    needs_survey: ['address_line_one', 'city', 'postcode', 'survey_required'],
    needs_pricing: ['address_line_one', 'city', 'postcode', 'estimated_hire_duration_days'],
    tendered: ['estimated_job_start', 'estimated_hire_duration_days', 'estimated_job_end', 'estimated_value', 'address_line_one', 'city', 'postcode'],
    closed_won: [
        'estimated_job_start',
        'estimated_hire_duration_days',
        'estimated_job_end',
        'estimated_value',
        'address_line_one',
        'city',
        'postcode',
        'is_primary',
    ],
    booked: [
        'estimated_job_start',
        'estimated_hire_duration_days',
        'estimated_job_end',
        'estimated_value',
        'address_line_one',
        'city',
        'postcode',
        'is_primary',
    ],
    invoiced: ['estimated_value'],
    extra_hire: ['additional_hire_start_date', 'additional_hire_end_date', 'additional_hire_duration', 'additional_hire_estimated_value'],
    archived: [],
};

/**
 * Optional fields to show for each status (not required, but visible)
 */
export const optionalFieldsByStatus: Record<string, string[]> = {
    needs_survey: ['address_line_two', 'county', 'country', 'licence_required'],
    needs_pricing: ['address_line_two', 'county', 'country', 'licence_required', 'weekly_inspections'],
    tendered: ['address_line_two', 'county', 'country', 'licence_required', 'weekly_inspections'],
    closed_won: ['address_line_two', 'county', 'country', 'licence_required', 'weekly_inspections'],
    booked: ['address_line_two', 'county', 'country', 'licence_required', 'weekly_inspections'],
    invoiced: [],
    extra_hire: ['invoiced', 'invoiced_date'],
    awaiting_takedown: ['invoiced', 'invoiced_date'],
    archived: [],
};

/**
 * Address field names for checking nested address structure
 */
export const addressFieldNames = ['address_line_one', 'address_line_two', 'city', 'county', 'postcode', 'country'];

/**
 * Statuses that require a site survey to be completed
 */
export const statusesThatRequireSiteSurvey = ['needs_pricing', 'tendered', 'closed_won', 'booked', 'invoiced', 'extra_hire', 'awaiting_takedown'];

/**
 * Statuses that require the lead to have a converted job (past 'booked')
 */
export const statusesThatRequireJob = ['invoiced', 'extra_hire', 'awaiting_takedown'];

/**
 * Statuses that show the PhasesList component for managing job phases
 */
export const statusesThatShowPhases = ['closed_won', 'booked', 'invoiced', 'extra_hire', 'awaiting_takedown'];

/**
 * Check if a field should be required for a given status
 */
export function isFieldRequiredForStatus(fieldName: string, status: string | undefined | null): boolean {
    if (!status) return false;
    const requiredFields = requiredFieldsByStatus[status] || [];
    return requiredFields.includes(fieldName);
}

/**
 * Get all required fields for a status
 */
export function getRequiredFieldsForStatus(status: string | undefined | null): string[] {
    if (!status) return [];
    return requiredFieldsByStatus[status] || [];
}

/**
 * Get all optional fields for a status
 */
export function getOptionalFieldsForStatus(status: string | undefined | null): string[] {
    if (!status) return [];
    return optionalFieldsByStatus[status] || [];
}
